import { useEffect, useState } from "react";

import { SectionIntro } from "@/components/SectionIntro";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { fetchGlossary } from "@/lib/api";
import { pickText } from "@/lib/content";

export const GlossaryPage = ({ language }) => {
  const [query, setQuery] = useState("");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const data = await fetchGlossary({ q: query || undefined });
        setEntries(data);
        setError("");
      } catch (error) {
        console.error(error);
        setEntries([]);
        setError(pickText(language, "The glossary is unavailable right now.", "Il glossario non è disponibile in questo momento."));
        toast.error(pickText(language, "Glossary search failed.", "La ricerca del glossario non è riuscita."));
      } finally {
        setLoading(false);
      }
    }, 220);

    return () => clearTimeout(timer);
  }, [query]);

  return (
    <div className="space-y-10" data-testid="glossary-page">
      <SectionIntro
        description={pickText(
          language,
          "A translation matrix for ceremonial and conceptual terms used across the atlas.",
          "Una matrice di traduzione per i termini cerimoniali e concettuali usati nell'atlante.",
        )}
        eyebrow={pickText(language, "Glossary", "Glossario")}
        testId="glossary-intro"
        title={pickText(language, "Italian and English in parallel.", "Italiano e inglese in parallelo.")}
      />

      <div className="glass-card rounded-[2rem] p-5 md:p-6" data-testid="glossary-search-panel">
        <Input
          className="h-12 rounded-full border-white/10 bg-black/20 px-5 text-white"
          data-testid="glossary-search-input"
          onChange={(event) => setQuery(event.target.value)}
          placeholder={pickText(language, "Search a term or context", "Cerca un termine o un contesto")}
          value={query}
        />
      </div>

      {loading ? (
        <div className="glass-card rounded-[2rem] p-5 text-sm text-slate-300" data-testid="glossary-loading-state">
          {pickText(language, "Loading glossary…", "Caricamento glossario…")}
        </div>
      ) : null}

      {error ? (
        <div className="glass-card rounded-[2rem] border-red-500/20 p-5 text-sm text-red-200" data-testid="glossary-error-state">
          {error}
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2" data-testid="glossary-grid">
        {entries.map((entry) => (
          <div className="glass-card rounded-[2rem] p-6" data-testid={`glossary-card-${entry.id}`} key={entry.id}>
            <p className="section-label" data-testid={`glossary-it-${entry.id}`}>
              {entry.term_it}
            </p>
            <h3 className="mt-4 text-3xl text-foreground" data-testid={`glossary-en-${entry.id}`}>
              {entry.term_en}
            </h3>
            <p className="mt-4 text-sm leading-7 text-slate-300" data-testid={`glossary-context-${entry.id}`}>
              {language === "it" ? entry.context_it : entry.context_en}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};